// Nepal Travel Lifecycle — 22 stages, structured knowledge graph spine.
// Each stage: coverage %, route, sections (L1/L2/L3), lifecycle flow.
import { EXPLORE_IMAGES } from './exploreImages';
import { OFFICIAL_SOURCES, LAST_VERIFIED } from './officialSources';

export const LIFECYCLE_PHASES = [
	{ id: 'before', name: 'Before You Travel', stages: ['dream', 'research', 'choose_season', 'choose_destination', 'visa', 'flights', 'insurance', 'packing'] },
	{ id: 'arrival_phase', name: 'Arrival & Setup', stages: ['arrival', 'sim', 'atm', 'transport', 'hotel'] },
	{ id: 'during', name: 'During Your Trip', stages: ['sightseeing', 'shopping', 'food', 'festivals', 'adventure'] },
	{ id: 'after', name: 'Leaving Nepal', stages: ['departure', 'souvenirs', 'return_home'] },
];

export const LIFECYCLE_STAGES = [
	{
		id: 'dream',
		name: 'Dream',
		slug: 'dream',
		question: 'Why should I visit Nepal?',
		icon: '✨',
		coverage: 35,
		status: 'partial',
		route: '/plan/dream',
		dedicated: false,
		hero: EXPLORE_IMAGES.himalaya,
		quickFacts: [
			{ value: '8 of 10', label: 'World\'s highest peaks' },
			{ value: '10', label: 'UNESCO sites' },
			{ value: '80+', label: 'Ethnic groups' },
			{ value: '365', label: 'Days of festivals' },
		],
		lead: 'Nepal is not one destination — it is the Himalaya, ancient cities, jungle safaris, pilgrimage routes, and village festivals in a country the size of Arkansas.',
		sections: [
			{ id: 'why', question: 'What makes Nepal unique?', answer: 'The only country with eight of the world\'s 14 peaks above 8,000m. Home to Everest, Annapurna, Lumbini (Buddha\'s birthplace), living goddess Kumari, and more festivals than days in the year. Nowhere else combines teahouse trekking at 5,000m with medieval city squares at 1,400m in a single two-week trip.', tips: ['Nepal rewards slow travel — rushing between cities misses the point.', 'Most first-timers underestimate how much diversity fits in 10–14 days.'] },
			{ id: 'who', question: 'Who is Nepal best for?', answer: 'Trekkers, culture seekers, pilgrims, wildlife watchers, photographers, families (with planning), budget backpackers, and luxury travelers (helicopter treks, Dwarika\'s). Less ideal for beach holidays or nightlife-focused trips.', tips: ['Solo female travelers commonly trek Annapurna and Everest — standard precautions apply.', 'Seniors can enjoy Kathmandu Valley and Pokhara without high altitude.'] },
		],
		faqs: [
			{ q: 'Is Nepal safe for tourists?', a: 'Nepal is generally safe for tourists. Standard precautions apply in cities. Trekking risks are primarily altitude and weather — not crime. Check your government travel advisory before booking.' },
			{ q: 'Do I need to be super fit?', a: 'No for cultural tours. Moderate fitness for short treks. Good fitness for Everest Base Camp or high passes.' },
		],
	},
	{
		id: 'research',
		name: 'Research Nepal',
		slug: 'research',
		question: 'What is Nepal — regions, culture, geography?',
		icon: '🗺',
		coverage: 30,
		status: 'partial',
		route: '/destinations',
		dedicated: true,
		redirectLabel: 'Explore destinations & provinces',
	},
	{
		id: 'choose_season',
		name: 'Choose Season',
		slug: 'choose-season',
		question: 'When should I visit?',
		icon: '📅',
		coverage: 58,
		status: 'partial',
		route: '/seasonal-discovery',
		dedicated: true,
		redirectLabel: 'Seasonal Discovery Hub',
	},
	{
		id: 'choose_destination',
		name: 'Choose Destination',
		slug: 'choose-destination',
		question: 'Where should I go?',
		icon: '📍',
		coverage: 18,
		status: 'partial',
		route: '/destinations',
		dedicated: true,
		redirectLabel: 'Destination guides',
	},
	{
		id: 'visa',
		name: 'Visa',
		slug: 'visa',
		question: 'Do I need a visa?',
		icon: '🛂',
		coverage: 72,
		status: 'partial',
		route: '/visa',
		dedicated: true,
		redirectLabel: 'Visa & Permits Hub',
	},
	{
		id: 'flights',
		name: 'Flights',
		slug: 'flights',
		question: 'How do I get to Nepal?',
		icon: '✈️',
		coverage: 45,
		status: 'partial',
		route: '/plan/flights',
		dedicated: false,
		hero: EXPLORE_IMAGES.himalaya,
		quickFacts: [
			{ value: 'KTM', label: 'Tribhuvan International Airport' },
			{ value: '30+', label: 'Airlines serve Kathmandu' },
			{ value: 'Doha/Delhi', label: 'Common hubs' },
			{ value: 'Lukla', label: 'Separate domestic flight' },
		],
		lead: 'Almost all international visitors fly into Tribhuvan International Airport (KTM), Kathmandu. There are no direct flights from most Western cities — you connect through Delhi, Doha, Istanbul, Bangkok, or Kuala Lumpur.',
		sections: [
			{ id: 'airports', question: 'Which airports serve Nepal?', answer: 'Tribhuvan International Airport (KTM) is the only international airport for scheduled passenger flights. Pokhara International Airport and Gautam Buddha International Airport (Lumbini) accept some international flights but KTM remains the main entry point.', source: OFFICIAL_SOURCES.caan, tips: ['Confirm your arrival airport before booking — most tickets say Kathmandu/KTM.'] },
			{ id: 'airlines', question: 'Which airlines fly to Kathmandu?', answer: 'Major carriers include Qatar Airways, Turkish Airlines, Air India, Nepal Airlines, Himalaya Airlines, Singapore Airlines (via connections), Thai Airways, and others. Routes change seasonally — search KTM on Google Flights or Skyscanner.', tips: ['Book 2–3 months ahead for October peak season.', 'Eastbound routes via Bangkok often cheaper than European hubs.'] },
			{ id: 'lukla', question: 'What about the Lukla (Everest) flight?', answer: 'Lukla is a separate domestic flight from Kathmandu (or Ramechhap in peak season). It is NOT your international arrival. Book through domestic airlines (Tara/Yeti/Buddha) or your trekking agency after arriving in Kathmandu.', source: OFFICIAL_SOURCES.caan, tips: ['Never plan to trek on the same day you fly internationally into KTM.', 'Weather delays of 1–3 days are normal on Lukla routes.'] },
			{ id: 'baggage', question: 'Baggage limits?', answer: 'International flights: standard airline limits (typically 23kg checked). Domestic mountain flights: often 10–15kg strict limit. Pack a separate small bag for Lukla.', tips: ['Store excess luggage at your Kathmandu hotel during treks.'] },
		],
		faqs: [
			{ q: 'Are there direct flights from Europe?', a: 'Rare and seasonal. Most Europeans connect via Istanbul, Doha, or Delhi. Germans often route through Delhi or Doha.' },
			{ q: 'Can I enter by land from India?', a: 'Yes — Sunauli, Birgunj, Kakarbhitta, and other border crossings. Get visa on arrival at the border if eligible.' },
		],
	},
	{
		id: 'insurance',
		name: 'Insurance',
		slug: 'insurance',
		question: 'What insurance do I need?',
		icon: '🛡',
		coverage: 45,
		status: 'partial',
		route: '/plan/insurance',
		dedicated: false,
		hero: EXPLORE_IMAGES.trekking,
		quickFacts: [
			{ value: '5,000m+', label: 'Altitude cover needed' },
			{ value: '$5k–15k', label: 'Heli rescue without cover' },
			{ value: 'Essential', label: 'For trekking' },
		],
		lead: 'Travel insurance is not legally required but is essential for trekking. Standard policies often exclude altitude above 3,000m — you need a trekking-specific policy.',
		sections: [
			{ id: 'why', question: 'Why is insurance critical in Nepal?', answer: 'Helicopter evacuation from Everest or Annapurna regions costs $5,000–$15,000+ without insurance. Hospital treatment for altitude sickness, fractures, or GI illness requires upfront payment at private clinics. Search-and-rescue in mountains is helicopter-dependent.', tips: ['Read the altitude limit in your policy — "worldwide" is not enough.', 'World Nomads, True Traveller, and specialist mountaineering insurers are commonly used.'] },
			{ id: 'cover', question: 'What should my policy cover?', answer: 'Minimum: emergency medical expenses, helicopter evacuation, altitude limit matching your trek (5,500m+ for EBC), trip cancellation, and personal liability. Adventure sports add-on for paragliding, rafting, bungee.', tips: ['Declare pre-existing conditions honestly.', 'Keep policy number and emergency phone saved offline.'] },
			{ id: 'rescue', question: 'How does helicopter rescue work?', answer: 'Your guide or lodge contacts rescue company. Insurance company authorizes payment. Without insurance, you pay upfront — lodges will help arrange credit but costs are severe.', tips: ['CIWEC Clinic in Kathmandu is the standard expat hospital for insurance billing.', 'Carry insurance card photo on phone and paper copy.'] },
		],
		faqs: [
			{ q: 'Is insurance required for a tourist visa?', a: 'Not legally required for visa, but strongly recommended. Some trekking agencies require proof of insurance.' },
			{ q: 'Does EU health insurance cover Nepal?', a: 'EHIC/GHIC does not cover Nepal. You need separate travel insurance.' },
		],
	},
	{
		id: 'packing',
		name: 'Packing',
		slug: 'packing',
		question: 'What should I bring?',
		icon: '🎒',
		coverage: 45,
		status: 'partial',
		route: '/plan/packing',
		dedicated: false,
		hero: EXPLORE_IMAGES.trekking,
		quickFacts: [
			{ value: 'Layers', label: 'Key packing rule' },
			{ value: 'Type C', label: 'Power adapter' },
			{ value: 'USD cash', label: 'For visa & teahouses' },
		],
		lead: 'What you pack depends entirely on whether you are doing a city cultural tour, a teahouse trek, or both. See the Seasonal Discovery hub for season-specific lists.',
		sections: [
			{ id: 'essentials', question: 'Universal essentials for Nepal', answer: 'Passport (6+ months validity), printed visa barcode, USD cash for visa, travel insurance documents, power adapter (Type C/D), sunscreen, hand sanitizer, reusable water bottle, head torch, basic first aid.', tips: ['Pack medications in original containers for customs.', 'Down jacket can be rented cheaply in Thamel — don\'t overpack.'] },
			{ id: 'trekking', question: 'Trekking-specific gear', answer: 'Broken-in hiking boots, merino base layers, fleece, down jacket, rain shell, gaiters (monsoon), sleeping bag rated to -10°C for high treks (or rent in Kathmandu), trekking poles, sunglasses, SPF 50, lip balm, Diamox (consult doctor).', tips: ['Buy maps offline — Gaia GPS or Maps.me with Nepal tiles.', 'Cash for teahouses — cards not accepted on trails.'] },
			{ id: 'city', question: 'City/cultural tour packing', answer: 'Modest clothing for temples (cover shoulders and knees), comfortable walking shoes, light rain jacket, daypack, camera, universal adapter. Leave valuables in hotel safe.', tips: ['Shawls/scarves for temple visits — buy locally if needed.', 'October: warm days, cool evenings in Kathmandu.'] },
		],
		faqs: [
			{ q: 'Can I buy gear in Kathmandu?', a: 'Yes — Thamel has extensive trekking gear at lower prices. Quality varies — inspect zippers and seams. Renting down jackets is common.' },
		],
		related: [{ label: 'Seasonal packing guide', to: '/seasonal-discovery' }],
	},
	{
		id: 'arrival',
		name: 'Arrival',
		slug: 'arrival',
		question: 'What happens when I land?',
		icon: '🛬',
		coverage: 68,
		status: 'partial',
		route: '/arrival',
		dedicated: true,
		redirectLabel: 'Kathmandu Airport Arrival Guide',
	},
	{
		id: 'sim',
		name: 'SIM & Connectivity',
		slug: 'sim',
		question: 'How do I get online?',
		icon: '📱',
		coverage: 55,
		status: 'partial',
		route: '/plan/sim',
		dedicated: false,
		hero: EXPLORE_IMAGES.valley,
		quickFacts: [
			{ value: 'NTC / Ncell', label: 'Main carriers' },
			{ value: 'Passport', label: 'Required for SIM' },
			{ value: '4G', label: 'KTM & Pokhara' },
		],
		lead: 'Two main carriers: Nepal Telecom (NTC) and Ncell. Both offer tourist prepaid SIMs. Registration requires passport copy per Nepal telecom regulations.',
		sections: [
			{ id: 'choice', question: 'NTC vs Ncell — which to choose?', answer: 'Ncell generally has faster data in Kathmandu and Pokhara. NTC has broader rural coverage on some trekking routes. For a standard tourist trip, either works — compare packages at the counter.', source: OFFICIAL_SOURCES.ntc, tips: ['Buy at airport or Thamel — prices similar.', 'Ncell often preferred for data speed in cities.'] },
			{ id: 'packages', question: 'Typical tourist packages', answer: 'Tourist SIM packages typically include 7–30 days with 5–20GB data. Prices change frequently — check ntc.net.np and ncell.com.np for current packs. Recharge via shops, apps, or USSD codes.', source: OFFICIAL_SOURCES.ncell, tips: ['Save offline maps before losing WiFi.', 'Trekking: signal drops above Namche — don\'t rely on data on trail.'] },
			{ id: 'esim', question: 'eSIM options', answer: 'NTC offers eSIM via esim.ntc.net.np for compatible phones. Activate over WiFi after landing. International eSIM providers (Airalo, etc.) work for arrival but local SIM is cheaper for longer stays.', source: OFFICIAL_SOURCES.ntc, tips: ['Activate eSIM using airport WiFi before leaving terminal.'] },
			{ id: 'registration', question: 'Registration requirements', answer: 'Passport copy and photo required for SIM registration per Nepal telecommunications regulations. One SIM per passport typical.', tips: ['Hotel address needed for registration form.'] },
		],
		faqs: [
			{ q: 'Will my phone work?', a: 'Unlocked phones work with local SIM. Check your phone is not carrier-locked before travel.' },
			{ q: 'Is WiFi reliable?', a: 'Hotels and cafes in cities have WiFi. Trekking lodges have slow/intermittent WiFi. NT WiFi hotspots exist at Kathmandu Airport.' },
		],
		related: [{ label: 'Airport arrival guide', to: '/arrival' }, { label: 'Full SIM guide', to: '/guides/getting-a-sim-card-in-nepal' }],
	},
	{
		id: 'atm',
		name: 'ATM & Money',
		slug: 'atm',
		question: 'How do I access cash and pay?',
		icon: '💰',
		coverage: 55,
		status: 'partial',
		route: '/plan/atm',
		dedicated: false,
		hero: EXPLORE_IMAGES.valley,
		quickFacts: [
			{ value: 'NPR', label: 'Nepali Rupee' },
			{ value: 'USD', label: 'Visa & backup cash' },
			{ value: 'ATM fees', label: 'NPR 250–500 typical' },
		],
		lead: 'Nepal uses the Nepali Rupee (NPR). Cards work in cities; cash is essential for trekking, markets, and small shops.',
		sections: [
			{ id: 'currency', question: 'What currency should I bring?', answer: 'USD cash for visa on arrival and as backup. Exchange to NPR after arrival. It is illegal to import/export NPR notes. ATMs dispense NPR — your bank charges foreign transaction fees.', tips: ['Bring USD notes from 2006 or newer — older bills rejected.', 'Notify your bank before travel.'] },
			{ id: 'atm', question: 'Where are ATMs?', answer: 'ATMs widely available in Kathmandu, Pokhara, and major towns. Nabil, Himalayan, Standard Chartered, and others. Daily limits typically NPR 10,000–35,000. Airport has ATMs after immigration.', tips: ['Withdraw larger amounts in cities before trekking — trail ATMs rare and unreliable.', 'Carry two cards from different banks.'] },
			{ id: 'cards', question: 'Card acceptance', answer: 'Major hotels, some Thamel shops, and upscale restaurants accept Visa/Mastercard with 3–4% surcharge. Markets, teahouses, taxis, and local buses are cash-only.', tips: ['Always carry NPR 5,000–10,000 in city; NPR 30,000+ for multi-day treks.'] },
			{ id: 'tipping', question: 'Tipping culture', answer: 'Not mandatory but appreciated. Guides and porters: tip at trek end (USD 50–150+ depending on group size and trek length). Restaurants: round up. Taxis: round up.', tips: ['Agree porter tip expectations with agency before trek.'] },
		],
		faqs: [
			{ q: 'How much cash for a 2-week trip?', a: 'City only: NPR 30,000–50,000 plus card for hotels. Trekking add NPR 3,000–5,000/day for teahouse food and lodging.' },
		],
		related: [{ label: 'Currency guide', to: '/guides/currency-atms-money-in-nepal' }, { label: 'Airport arrival', to: '/arrival' }],
	},
	{
		id: 'transport',
		name: 'Transport',
		slug: 'transport',
		question: 'How do I move around?',
		icon: '🚌',
		coverage: 48,
		status: 'partial',
		route: '/transportation',
		dedicated: true,
		redirectLabel: 'Transportation Hub',
	},
	{
		id: 'hotel',
		name: 'Hotel',
		slug: 'hotel',
		question: 'Where should I stay?',
		icon: '🏨',
		coverage: 40,
		status: 'partial',
		route: '/plan/hotel',
		dedicated: false,
		hero: EXPLORE_IMAGES.valley,
		quickFacts: [
			{ value: 'Thamel', label: 'KTM tourist hub' },
			{ value: 'Lakeside', label: 'Pokhara' },
			{ value: '$10–300', label: 'Nightly range' },
		],
		lead: 'Where you stay depends on your itinerary. Kathmandu (Thamel), Pokhara (Lakeside), and Chitwan (Sauraha) are the three main tourist bases.',
		sections: [
			{ id: 'kathmandu', question: 'Where to stay in Kathmandu', answer: 'Thamel: tourist hub, restaurants, gear shops, walkable. Lazimpat/Baluwatar: upscale, embassies, quieter. Boudha: near stupa, Tibetan culture. Bhaktapur: heritage atmosphere, farther from airport.', tips: ['Book first night before arrival — prepaid airport pickup easiest.', 'October peak: book 2–4 weeks ahead.'] },
			{ id: 'pokhara', question: 'Where to stay in Pokhara', answer: 'Lakeside: tourist strip along Phewa Lake, restaurants, agencies. North Lakeside quieter. Damside cheaper.', tips: ['Lakeside is walkable — no taxi needed for restaurants.'] },
			{ id: 'trekking', question: 'Accommodation on treks', answer: 'Teahouse lodges every few hours on major routes. No booking needed — walk in. Cost NPR 200–500/night (room only). Food ordered separately. Quality improves with price.', tips: ['Bring sleeping bag liner for hygiene.', 'Hot shower costs NPR 200–500 extra.'] },
			{ id: 'booking', question: 'Book ahead or walk-in?', answer: 'Kathmandu/Pokhara peak season (Oct–Nov): book ahead. Off-season: walk-in fine. Trekking teahouses: no booking. Chitwan lodges: book ahead in peak season.', tips: ['Agoda, Booking.com work in cities. Teahouses not on booking platforms.'] },
		],
		faqs: [
			{ q: 'Is Thamel safe at night?', a: 'Generally yes — busy and well-lit. Standard urban precautions.' },
		],
	},
	{
		id: 'sightseeing',
		name: 'Sightseeing',
		slug: 'sightseeing',
		question: 'What should I see?',
		icon: '🏛',
		coverage: 35,
		status: 'partial',
		route: '/destinations',
		dedicated: true,
		redirectLabel: 'Destination guides',
	},
	{
		id: 'shopping',
		name: 'Shopping',
		slug: 'shopping',
		question: 'What should I buy?',
		icon: '🛍',
		coverage: 42,
		status: 'partial',
		route: '/shopping',
		dedicated: true,
		redirectLabel: 'Shopping Hub',
	},
	{
		id: 'food',
		name: 'Food',
		slug: 'food',
		question: 'What should I eat?',
		icon: '🍛',
		coverage: 45,
		status: 'partial',
		route: '/plan/food',
		dedicated: false,
		hero: EXPLORE_IMAGES.valley,
		quickFacts: [
			{ value: 'Dal bhat', label: 'National dish' },
			{ value: 'Momo', label: 'Must-try dumplings' },
			{ value: 'Newari', label: 'KTM Valley cuisine' },
		],
		lead: 'Nepali food is diverse — dal bhat (lentils and rice) is the daily staple, but regional cuisines from Newari, Tharu, Sherpa, and Tibetan traditions make every area different.',
		sections: [
			{ id: 'staples', question: 'What are the must-try dishes?', answer: 'Dal bhat power (lentils and rice — the daily national meal), momo (steamed or fried dumplings), thukpa (noodle soup), sel roti (sweet ring bread), chatamari (Newari rice crepe), yomari (steamed rice dumpling with filling), yak cheese (high mountains), gundruk (fermented leafy greens).', tips: ['Eat where locals eat — busy restaurants mean fresh food.', 'Dal bhat is unlimited refill at most local places.'] },
			{ id: 'dietary', question: 'Vegetarian, vegan, halal?', answer: 'Nepal is vegetarian-friendly — dal bhat is naturally veg. Vegan harder (ghee in many dishes). Kathmandu has dedicated veg restaurants. Halal limited outside Muslim communities in the Terai.', tips: ['Specify "no meat, no fish" — chicken sometimes added to "veg".'] },
			{ id: 'safety', question: 'Food safety tips', answer: 'Drink bottled or purified water only. Avoid raw salads unless upscale restaurant. Peel fruit. Busy street food stalls safer than empty ones. Wash hands frequently.', tips: ['Avoid tap water including ice in cheap restaurants.', 'Carry oral rehydration salts.'] },
			{ id: 'regions', question: 'Regional cuisines', answer: 'Newari (Kathmandu Valley): bara, chatamari, yomari, samay baji feast. Tharu (Chitwan): fish, crab, local rice dishes. Sherpa (Everest): tsampa, butter tea, potato dishes. Tibetan: thukpa, tingmo, shabhaley (fried meat pastry). Terai: litti-chokha influence, sweets.', tips: ['Newari feast at a traditional restaurant in Patan is a cultural experience.'] },
			{ id: 'drinks', question: 'What to drink', answer: 'Chiya (sweet milk tea) everywhere. Butter tea in mountains. Tongba (warm millet beer) in Sherpa villages. Lassi and fresh juice in cities. Raksi (local spirit) at festivals — strong. Coffee culture growing in Kathmandu and Pokhara.', tips: ['Avoid untreated stream water on treks — use purification tablets.'] },
			{ id: 'trekking-food', question: 'Food on the trail', answer: 'Teahouse menus are similar: dal bhat, noodles, fried rice, eggs, porridge. Prices rise with altitude. Meat becomes less reliable above Namche. Garlic soup is a local altitude remedy. Breakfast is usually porridge, eggs, or Tibetan bread.', tips: ['Dal bhat is the best value and most calories for trekkers.', 'Carry trail snacks — bars, nuts, chocolate — for between-meal energy.'] },
		],
		faqs: [
			{ q: 'Can I drink tap water?', a: 'No — bottled, boiled, or filtered water only.' },
			{ q: 'Is street food safe?', a: 'Generally yes at busy stalls. Avoid uncooked items if you have a sensitive stomach.' },
		],
	},
	{
		id: 'festivals',
		name: 'Festivals',
		slug: 'festivals',
		question: 'What cultural events can I experience?',
		icon: '🎉',
		coverage: 45,
		status: 'partial',
		route: '/festivals',
		dedicated: true,
		redirectLabel: 'Festivals Hub',
	},
	{
		id: 'adventure',
		name: 'Adventure',
		slug: 'adventure',
		question: 'What treks and activities are available?',
		icon: '🏔',
		coverage: 50,
		status: 'partial',
		route: '/plan/adventure',
		dedicated: false,
		hero: EXPLORE_IMAGES.annapurna,
		quickFacts: [
			{ value: 'EBC / ABC', label: 'Classic treks' },
			{ value: 'TIMS', label: 'Permit required' },
			{ value: 'Oct–Nov', label: 'Peak trekking' },
		],
		lead: 'Nepal\'s adventure menu spans teahouse trekking, peak climbing, rafting, paragliding, jungle safari, mountain biking, and bungee jumping.',
		sections: [
			{ id: 'treks', question: 'Classic treks', answer: 'Everest Base Camp (12–14 days, high altitude), Annapurna Base Camp (7–10 days), Annapurna Circuit (15–18 days), Langtang Valley (7–10 days), Mardi Himal (5–7 days), Upper Mustang (10–14 days, restricted permit).', tips: ['October is peak season — book flights and lodges early.', 'ABC is shorter and lower than EBC — good first trek.'] },
			{ id: 'permits', question: 'Permits required', answer: 'TIMS card for most treks. National park entry (Sagarmatha, ACAP, etc.). Upper Mustang and Manaslu require restricted area permits through registered agencies.', source: OFFICIAL_SOURCES.ntb, tips: ['Agency handles permits for organized treks.', 'Independent trekkers buy TIMS in Kathmandu or Pokhara.'] },
			{ id: 'other', question: 'Beyond trekking', answer: 'Paragliding (Pokhara), white-water rafting (Trishuli, Bhote Koshi), Chitwan jungle safari, bungee (Bhote Koshi), mountain flights (Everest view), rock climbing (Nagarjun).', tips: ['Paragliding best Oct–Apr with clear views.'] },
		],
		faqs: [
			{ q: 'Do I need a guide?', a: 'Not legally required on most routes except restricted areas. Guide strongly recommended for first high-altitude trek.' },
		],
		related: [{ label: 'TIMS & permits', to: '/guides/tims-card-trekking-permits' }, { label: 'Seasonal trekking', to: '/seasonal-discovery' }],
	},
	{
		id: 'departure',
		name: 'Departure',
		slug: 'departure',
		question: 'How do I leave Nepal?',
		icon: '🛫',
		coverage: 18,
		status: 'partial',
		route: '/plan/departure',
		dedicated: false,
		hero: 'https://images.unsplash.com/photo-1548013146-72479768bada?w=1920&q=80',
		quickFacts: [
			{ value: '3 hrs', label: 'Arrive before flight' },
			{ value: 'NPR 1,130', label: 'TIA departure tax (approx)' },
			{ value: 'Export cert', label: 'For antiques' },
		],
		lead: 'Leaving Nepal is simpler than arriving — but allow time for airport queues, export checks on souvenirs, and visa overstay verification.',
		sections: [
			{ id: 'airport', question: 'Getting to the airport', answer: 'Prepaid taxi from Thamel ~NPR 800–1,000. Allow 45–90 minutes depending on traffic. International flights: arrive 3 hours before departure.', tips: ['Book taxi night before if you have an early flight.', 'Hotels arrange airport transfers.'] },
			{ id: 'tax', question: 'Airport departure tax', answer: 'Tribhuvan International Airport departure tax is typically included in international airline tickets — verify on your ticket. If not included, pay at airport counter.', tips: ['Keep visa and entry documents until fully departed.'] },
			{ id: 'export', question: 'Exporting souvenirs', answer: 'Antiques and cultural objects over 100 years old need Department of Archaeology export certificate. Keep purchase receipts. Customs may inspect bags.', source: OFFICIAL_SOURCES.customs, tips: ['Get export certificate in Kathmandu before packing antiques.'] },
			{ id: 'overstay', question: 'Visa overstay at departure', answer: 'Overstay fines (USD 5/day) must be paid at Department of Immigration before departure if you overstayed. Do not arrive at airport with expired visa unpaid.', source: OFFICIAL_SOURCES.immigration, tips: ['Visit immigration office 1–2 days before flight if you overstayed.'] },
		],
		faqs: [
			{ q: 'Can I take Nepali rupees home?', a: 'It is illegal to export NPR notes. Exchange remaining NPR before departure.' },
		],
	},
	{
		id: 'souvenirs',
		name: 'Souvenirs',
		slug: 'souvenirs',
		question: 'What can I take home?',
		icon: '🎁',
		coverage: 35,
		status: 'partial',
		route: '/shopping',
		dedicated: true,
		redirectLabel: 'Shopping & export rules',
	},
	{
		id: 'return_home',
		name: 'Return Home',
		slug: 'return-home',
		question: 'What do I need after the trip?',
		icon: '🏠',
		coverage: 10,
		status: 'partial',
		route: '/plan/return-home',
		dedicated: false,
		hero: EXPLORE_IMAGES.himalaya,
		quickFacts: [
			{ value: 'Re-entry', label: 'Multiple-entry visa' },
			{ value: 'Photos', label: 'Share & inspire' },
			{ value: 'Review', label: 'Help future travelers' },
		],
		lead: 'After Nepal — visa considerations for return visits, sharing your experience, and staying connected to the community.',
		sections: [
			{ id: 'reentry', question: 'Can I come back?', answer: 'Tourist visas are multiple-entry within validity period. If your 90-day visa has expired, apply fresh on next arrival. Maximum 150 days per calendar year.', source: OFFICIAL_SOURCES.immigration, tips: ['Count your days if planning return within same year.'] },
			{ id: 'health', question: 'Health after trekking', answer: 'Altitude effects can linger 1–2 weeks. See a doctor if persistent cough, fatigue, or chest pain after high-altitude trek.', tips: ['DVT risk on long flights after trekking — move legs on plane.'] },
			{ id: 'community', question: 'Stay connected', answer: 'Share honest reviews of teahouses, guides, and agencies — helps future travelers. Connect with VisitAllNepal community for return trip planning.', tips: ['Photography: respect privacy of locals when sharing online.'] },
		],
		faqs: [
			{ q: 'How do I plan my return trip?', a: 'Start with Seasonal Discovery for timing, then destinations you missed. Most return visitors do Langtang, Mustang, or Bardia on second trip.' },
		],
	},
];

export function getStageById(id) {
	return LIFECYCLE_STAGES.find((s) => s.id === id);
}

export function getStageBySlug(slug) {
	return LIFECYCLE_STAGES.find((s) => s.slug === slug);
}

export function adjacentStages(id) {
	const idx = LIFECYCLE_STAGES.findIndex((s) => s.id === id);
	return {
		prev: idx > 0 ? LIFECYCLE_STAGES[idx - 1] : null,
		next: idx < LIFECYCLE_STAGES.length - 1 ? LIFECYCLE_STAGES[idx + 1] : null,
	};
}

export function lifecycleFlowFor(id) {
	const { prev, next } = adjacentStages(id);
	const steps = [];
	if (prev) steps.push({ label: prev.name, to: prev.route, desc: 'Previous step' });
	const current = getStageById(id);
	if (current) steps.push({ label: current.name, to: current.route, desc: 'You are here' });
	if (next) steps.push({ label: next.name, to: next.route, desc: 'Next step' });
	return steps;
}

export const PLATFORM_META = {
	score: 48,
	lastAudit: LAST_VERIFIED,
	stagesTotal: 22,
	stagesPartial: 18,
	stagesDedicated: 12,
};
